<template>
  <CustomContainer
    :customClass="`externalThreeViewer ${modelName}`"
    elementType="div"
  >
  </CustomContainer>
</template>

  <script>
import { Vue, Component, Prop } from "vue-property-decorator";
import CustomContainer from "@/components/Globals/CustomContainer.vue";
import { screenSize } from "@/libs";
import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import camera from "@/mixins/Customizer/SceneSetup/Camera";
import { getUGCPreviewEntries } from "@/components/molecule.external.3dviewer.helpers";
import sceneHelpers from "@vfdp-customs/customs-scene-helpers";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";

@Component({
  components: {
    CustomContainer,
  },
  mixins: [camera],
})
export default class ExternalViewer extends Vue {
  @Prop()
  modelName;
  @Prop()
  recipeData;
  @Prop()
  canvasWidth;
  @Prop()
  canvasHeight;

  pdm = {};
  scene = new THREE.Scene();
  modelObj = {};
  recipe_v2 = {};
  swatches = {};
  sceneHelpers = null;
  objLoader = new GLTFLoader();
  dracoLoader = new DRACOLoader();
  renderer = null;
  previewComplete = false;

  get domain() {
    return this.$store.state.utils.configuration.domain
      ? this.$store.state.utils.configuration.domain
      : process.env.VUE_APP_domain;
  }

  get ugcTextures() {
    return getUGCPreviewEntries(this.recipeData);
  }

  get isMobile() {
    return this.$store.getters["utils/returnScreenSize"].includes("mobile");
  }

  async createScene(modelName) {
    if (!modelName) return;
    try {
      await this.loadScene(modelName);
    } catch (error) {
      console.error(`Error loading Change Style preview for ${modelName}:`, error);
    } finally {
      this.finishPreview();
    }
  }

  async loadScene(modelName) {
    this.sceneHelpers = {
      ...new sceneHelpers({
        THREE,
      }).sceneHelpers,
    };
    const { env: utilsEnv } = this.$store.state.utils;
    const { VUE_APP_ENDPOINTS: base } = process.env;
    const pdmUrl = `${this.domain}${base.replace(
      /\{ENV\}/g,
      utilsEnv
    )}/global_pdms/${modelName}.json`;
    const responsePDM = await fetch(
      this.$store.getters["utils/addCacheBusting"](pdmUrl)
    );
    this.pdm = await responsePDM.json();
    this.sceneHelpers.setPdm({ pdm: this.pdm });
    this.sceneHelpers.swatchesRoot =
      this.$store.getters["endPoints/swatchesPath"]();
    this.initDracoDecoder({ domain: this.domain, env: utilsEnv });
    this.objLoader.setDRACOLoader(this.dracoLoader);
    this.setupRenderer(modelName);
    this.setupExternalCamera();
    this.setupExternalLights();
    await this.$nextTick();
    const swatchMaterialGroupUrl =
      this.$store.state.endPoints.customizer.swatchMaterialGroups;
    const url = `${swatchMaterialGroupUrl}${modelName}?caid=${this.$store.state.utils.caid}`;
    return fetch(this.$store.getters["utils/addCacheBusting"](url))
        .then((response) => response.json())
        .then((groups) => {
          this.sceneHelpers.setSwatchMaterialGroupsData({ groups });
          return this.create3Dmodel();
        })
        .then((modelObj) => {
          this.modelObj = modelObj;
          this.sceneHelpers.setModel({ model: this.modelObj });
          this.modelObj.position.set(0, 0, 0);
          this.sceneHelpers.parseModelComponents(this.modelObj);
          this.scene.position.set(0, 0, 0);
          this.scene.scale.set(1, 1, 1);
          this.scene.add(modelObj);
          this.sceneHelpers.setScene({ scene: this.scene });
          //this.sceneHelpers.setDefaultTextures({default_textures: this.default_textures});
          this.updateCameraPosition();
          this.controls.update();
          this.resizeCanvas();
          //this.controls.addEventListener('change', this.animate3DModel );
          return this.loadStitches();
        })
        .then(() => {
          this.recipe_v2 = this.recipeData;
          this.sceneHelpers.setRecipe({ recipe_v2: this.recipeData });
          const components = this.recipe_v2.components;
          const swatchesID = [
            ...new Set(
              Object.keys(components).map(
                (component) => components[component].swatch_id
              )
            ),
          ].toString();
          const url = `${this.$store.state.endPoints.customizer.availableSwatchesById}${swatchesID}?caid=${this.$store.state.utils.caid}`;
          return fetch(this.$store.getters["utils/addCacheBusting"](url))
            .then((response) => response.json())
            .then((swatchesObj) => {
              const swatchAry = [];
              Object.keys(swatchesObj).forEach((id) => {
                swatchAry.push(swatchesObj[id]);
              });
              this.sceneHelpers.setSwatches({ swatches: swatchAry });
              this.swatches = swatchesObj;
              const promises = Object.keys(components).map(async (componentName) => {
                const componentPDM = this.pdm.components[componentName];
                const swatchId = components[componentName].swatch_id;
                const componentGroup = componentPDM.component_configuration_group || [];
                const templateMaterial = null;
                const geometryUpdates = this.swatches?.[swatchId]?.geometry_updates;
                if (geometryUpdates) {
                  await this.applyGeoUpdates(geometryUpdates, componentName);
                }
                return new Promise((resolve, reject) => {
                  try {
                    this.sceneHelpers.changeSwatch(
                      {
                        swatchId,
                        componentGroup,
                        componentName,
                        templateMaterial,
                      },
                      async () => {
                        try {
                          this.controls.update();
                          await this.$nextTick();
                          const normalRepeat = this.swatches?.[swatchId]?.normal_repeat;
                          if (normalRepeat) {
                            await this.delayTimer(100);
                            if (componentPDM.component_configuration_group) {
                              for (const componentOfGroup of componentPDM.component_configuration_group) {
                                const componentGeo = this.modelObj.getObjectByName(componentOfGroup);
                                if (componentGeo.material.userData?.shader) {
                                  componentGeo.material.userData.shader.uniforms.normalRepeat.value = normalRepeat;
                                }
                              }
                            } else {
                              const componentGeo = this.modelObj.getObjectByName(componentName);
                              if (componentGeo.material.userData?.shader) {
                                componentGeo.material.userData.shader.uniforms.normalRepeat.value = normalRepeat;
                              }
                            }
                            await this.delayTimer(100);
                          }
                          resolve();
                        } catch (error) {
                          reject(error);
                        }
                      }
                    );
                  } catch (error) {
                    reject(error);
                  }
                });
              });
              return Promise.all(promises);
            })
            .then(async () => {
              for (const { componentName, imageUrl } of this.ugcTextures) {
                const componentPDM = this.pdm.components[componentName];
                if (!componentPDM || !componentPDM.ugc) continue;

                const geoPath = `geometry_updates/component_ugc/${componentName}.glb`;
                const modelSKU = this.pdm.parent_model || this.pdm.sku;
                const size = this.$store.state.sceneSetup.modelBySize;
                const geoUrl = this.$store.getters["utils/addCacheBusting"](`${this.$store.state.endPoints.customizer.s3ModelsBucket}gltf_models/${modelSKU}/${size}/${geoPath}`);
                const newUGCGeo = await this.$model3D.loadObject(geoUrl);
                const geoObj = newUGCGeo.getObjectByName(componentName);
                const currentGeo = this.modelObj.children[0].getObjectByName(componentName);
                if (currentGeo) this.modelObj.children[0].remove(currentGeo);
                this.modelObj.children[0].add(geoObj);

                await this.applyUGCTexture({
                  imgUrl: imageUrl,
                  componentName,
                });
                await this.delayTimer(100);
                const normalRepeat = componentPDM.ugc.normal_repeat;
                geoObj.material.normalMap.repeat.set(normalRepeat, normalRepeat);
                if (geoObj.material.userData?.shader) {
                  geoObj.material.userData.shader.uniforms.normalRepeat.value = normalRepeat;
                }
                await this.delayTimer(100);
              }

              this.animate3DModel();
            });
        });
  }

  finishPreview() {
    if (this.previewComplete) return;
    this.previewComplete = true;
    this.$emit("ready", this.modelName);
    if (this.objLoader.dracoLoader) this.objLoader.dracoLoader.dispose();
    if (this.dracoLoader) this.dracoLoader.dispose();
    const viewer = document.querySelector(`.externalThreeViewer.${this.modelName}`);
    if (viewer && viewer.parentElement) {
      viewer.parentElement.removeAttribute("disabled");
    }
  }

  loadDefaultAssets() {
    const promises = [];
    const components = Object.keys(this.pdm.components);
    components.forEach((componentName) => {
      const componentObj = this.pdm.components[componentName];
      promises.push(
        this.loadDefaultGeometries({ componentObj, componentName })
      );
    });
    return new Promise((resolve) => {
      Promise.all(promises).then(resolve);
    });
  }

  initDracoDecoder({ domain, env }) {
    const { VUE_APP_PUBLIC_PATH: publicPath } = process.env;
    const decoderPath = `${publicPath.replace(
      /\{ENV\}/g,
      env
    )}/draco/gltf/`;
    this.dracoLoader.setDecoderPath(decoderPath);
    this.dracoLoader.setDecoderConfig({ type: 'wasm' });
    this.dracoLoader.setWorkerLimit(1);
  }

  create3Dmodel() {
    const bucketURL = this.$store.state.endPoints.customizer.s3ModelsBucket;
    const sizeToLoad =
      this.$store.state.utils.bucketSizes[this.pdm.ugcDefaultSize];
    const modelSKU = this.pdm.parent_model
      ? this.pdm.parent_model
      : this.pdm.sku;
    const newModelURL = `${bucketURL}gltf_models/${modelSKU}/${sizeToLoad}/${modelSKU}-${sizeToLoad}.glb`;
    const modelUrl = newModelURL;
    return new Promise((resolve, reject) => {
      this.loadObject(modelUrl)
        .then((modelObj) => {
          resolve(modelObj);
        })
        .catch(reject);
    });
  }

  loadObject(url) {
    return new Promise((resolve, reject) => {
      this.objLoader.load(
        url,
        (obj) => {
          const modelObj = obj.scene;
          resolve(modelObj);
        },
        undefined,
        reject
      );
    });
  }

  animate3DModel() {
    this.renderer.compile(this.scene, this.camera);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  resizeCanvas() {
    if (this.camera) {
      const width = this.canvasWidth;
      const height = this.canvasHeight;
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
      this.responsiveModelDefaults();
      this.controls.reset();
    }
  }

  setupRenderer(modelName) {
    const { alpha, antialias, high_resolution, setClearColor } =
      this.pdm.default_display.scene_defaults.renderer;
    const { precision, shadowMap, shadowMapSoft, shadowMapType } =
      high_resolution;
    const preserveDrawingBuffer =
      this.$store.getters["utils/getUrlParameterByName"](
        "preserveDrawingBuffer"
      ) === "true";
    const DPR = this.canvasWidth/this.canvasHeight;
    //const { innerWidth: WW, innerHeight: HH } = window;
    const WW = this.canvasWidth;
    const HH = this.canvasHeight;

    this.renderer = new THREE.WebGLRenderer({
      alpha,
      antialias,
      precision,

      preserveDrawingBuffer,
    });
    this.renderer.setSize(WW, HH);
    this.renderer.setPixelRatio(DPR);
    this.renderer.setViewport(0, 0, WW * DPR, HH * DPR);
    this.renderer.shadowMap.enabled = shadowMap;
    this.renderer.shadowMapSoft = shadowMapSoft;
    this.renderer.shadowMap.type = THREE[shadowMapType];
    if (
      modelName &&
      !document.querySelector(`.externalThreeViewer.${this.modelName}`)
        .firstChild
    ) {
      document
        .querySelector(`.externalThreeViewer.${this.modelName}`)
        .appendChild(this.renderer.domElement);
    }
  }

  setupExternalLights() {
    const { lighting } = this.pdm.default_display.scene_defaults;
    for (let [index, item] of lighting.entries()) {
      let light = new THREE[item.type]();
      if (light.groundColor)
        light.groundColor = new THREE.Color(item.groundColor) || null;
      if (light.skyColor)
        light.skyColor = new THREE.Color(item.skyColor) || null;
      if (item.name) light.name = item.name || `${item.type}-${index}`;
      if (light.color && light.type !== "HemisphereLight")
        light.color = new THREE.Color(item.color) || new THREE.Color("#ffffff");
      if (light.position && light.type !== "AmbientLight")
        light.position.set(
          item.position[0],
          item.position[1],
          item.position[2]
        );
      if (light.angle) light.angle = item.angle || Math.PI / 3;
      if (typeof light.distance !== "undefined")
        light.distance = item.distance || 0.0;
      if (light.intensity) light.intensity = item.intensity || null;
      if (light.decay) light.decay = item.decay || 0;
      if (light.penumbra) light.penumbra = item.penumbra || 0.0;
      if (item.castShadow) light.castShadow = item.castShadow || true;
      if (item.receiveShadow) light.receiveShadow = item.receiveShadow || true;
      if (light.shadow)
        light.shadow.mapSize.width = item.shadowMapSizeWidth || 512;
      if (light.shadow)
        light.shadow.mapSize.height = item.shadowMapSizeHeight || 512;
      this.scene.add(light);
    }
  }

  setupExternalCamera() {
    const { camera_settings } = this.pdm.default_display.scene_defaults;
    this.camera = new THREE.PerspectiveCamera(
      camera_settings.fov,
      this.canvasWidth / this.canvasHeight,
      camera_settings.near,
      camera_settings.far
    );

    this.camera.position.set(
      camera_settings.position[0],
      camera_settings.position[1],
      camera_settings.position[2]
    );
    this.camera.setFocalLength(camera_settings.setLens);
    this.camera.zoom = this.isMobile
      ? 2
      : 1.5;
    this.camera.aspect = this.canvasWidth / this.canvasHeight;
    this.camera.updateProjectionMatrix();
    this.setupExternalOrbitControls();
    if (
      this.$store.getters["utils/getUrlParameterByName"]("changeView") ===
      "true"
    )
      this.addChangeViewToGlobalEscope();
  }

  setupExternalOrbitControls() {
    const controls = this.pdm.default_display.scene_defaults.controls.desktop;
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = controls.enableDamping;
    this.controls.dampingFactor = 0.25;
    this.controls.enableZoom = controls.enableZoom;
    this.controls.maxPolarAngle = controls.maxPolarAngle > 0 ? controls.maxPolarAngle : Math.PI / 2;
    this.controls.minPolarAngle = controls.minPolarAngle > 0 ? controls.minPolarAngle : 0;
    this.controls.zoomSpeed = controls.zoomSpeed;
    this.controls.enableRotate = true;
    this.controls.rotateSpeed = controls.rotateSpeed;
    this.controls.enableKeys = controls.enableKeys;
    this.controls.enablePan = controls.enablePan;
    this.controls.autoRotateSpeed = controls.autoRotateSpeed;
    this.controls.autoRotate = false; // Set false so as not to conflict w/ intro animation
    this.controls.update();
  }


  responsiveModelDefaults() {
    const { responsive_defaults } = this.pdm.default_display.scene_defaults;
    let matchedDefaults = {};
    for (let item of responsive_defaults) {
      let query = "only screen";
      query = `${query} and (min-width: ${item.breakpoint}px)`;
      if (typeof item.orientation !== "undefined") {
        query = `${query} and (orientation: ${item.orientation})`;
      }
      if (window.matchMedia(query).matches) {
        matchedDefaults = item;
      }
    }
    this.modelObj.scale.set(
      matchedDefaults.scale.x,
      matchedDefaults.scale.y,
      matchedDefaults.scale.z
    );
    this.modelObj.position.set(
      matchedDefaults.position.x,
      matchedDefaults.position.y,
      matchedDefaults.position.z
    );
    if (typeof matchedDefaults.target !== "undefined") {
      this.controls.target.set(
        matchedDefaults.target.x,
        matchedDefaults.target.y,
        matchedDefaults.target.z
      );
    }
    this.controls.minDistance = matchedDefaults.minDistance;
    this.controls.maxDistance = matchedDefaults.maxDistance;
  }

  setupModelDefaults() {
    const components = Object.keys(this.pdm.components);
    components.forEach((componentName) => {
      const componentObj = this.pdm.components[componentName];
      if (!componentObj.default_texture) {
        this.applyDefaultMeshOptions(componentName, componentObj);
      }

      const componentMeshObject = this.modelObj.getObjectByName(componentName);
      if (!componentObj.default_texture && componentMeshObject) {
        this.createMaterialFromMeshOptions(
          componentName,
          componentMeshObject,
          componentObj
        );
      } else if (componentObj.default_mesh_options && componentMeshObject) {
        this.sceneHelpers.updateMaterialOptions({
          mesh: componentMeshObject,
          options: componentObj.default_mesh_options,
        });
      }

      if (!componentObj.visible && componentMeshObject) {
        componentMeshObject.visible = false;
      }

      // set component group parent child configuration
      if (componentObj.component_configuration_group) {
        this.$store.commit(
          "componentConfigGroup/SET_COMPONENT_PARENT_CHILD_CONFIGURATION",
          {
            parentComponent: componentName,
            componentGroup: componentObj.component_configuration_group,
          }
        );
      }
    });
  }

  loadStitches() {
    const promises = [];
    const bucketURL = this.$store.state.endPoints.customizer.s3ModelsBucket;
    const components = this.pdm.components;
    const sizeToLoad =
      this.$store.state.utils.bucketSizes[this.pdm.ugcDefaultSize];
    const modelSKU = this.pdm.parent_model
      ? this.pdm.parent_model
      : this.pdm.sku;
    const stitchesURL = `${bucketURL}gltf_models/${modelSKU}/${sizeToLoad}/stitches/`;
    Object.keys(components).forEach((name) => {
      if (components[name].stitches) {
        const stitches = components[name].stitches;
        promises.push(
          this.loadObject(`${stitchesURL}${stitches.url}`).then((geometry) => {
            const mesh = geometry.getObjectByName(`${name}_stitches`);
            this.modelObj.children[0].add(geometry.children[0].children[0]);
            this.sceneHelpers.updateMaterialOptions({
              mesh,
              options:
                this.pdm.components[name].stitches.material_options
                  .mesh_options,
            });
          })
        );
      }
    });

    return Promise.all(promises);
  }

  async applyUGCTexture({ imgUrl, componentName }) {
    const componentPDM = this.pdm.components[componentName];
    const merchandisingUGC = componentPDM.ugc;
    const material_options = {
      ...merchandisingUGC.ugc_swatch.material_options,
    };
    const textureList = JSON.parse(JSON.stringify(material_options.textures));
    textureList.map.image_url = imgUrl;
    textureList.map.options.colorSpace = 'SRGBColorSpace';

    textureList.normalMap.image_url = `${this.$store.getters[
      "endPoints/swatchesPath"
    ]()}${textureList.normalMap.image_url}`;
    const materialName = `ugc-${componentName}`;
    try {
      return this.loadMultipleTextures(textureList).then((loadedTextures) => {
        const component = this.sceneHelpers.getSceneObjectByName({
          objectName: componentName,
        });

        component.material = this.sceneHelpers.createMaterial({
          mesh_type: material_options.type,
          options: Object.assign(
            {},
            material_options.mesh_options,
            loadedTextures
          ),
          componentName,
          shadersType: 'uv1',
        });
      });
    } catch (err) {
      console.log(err, componentName, textureList);
    }
  }

  loadMultipleTextures(textureList) {
    const textureKeys = Object.keys(textureList);
    const promises = textureKeys.map((name) => {
      const data = textureList[name];
      const { loader } = this.sceneHelpers.getTextureLoader({
        manager: data.manager,
      });
      return new Promise((resolve, reject) => {
        this.sceneHelpers
          .loadTexture({
            path: data.image_url,
            options: data.options,
            textureLoader: loader,
          })
          .then((texture) => {
            texture.flipY = false;
            return resolve({ name, texture });
          })
          .catch((err) => {
            console.error("Error Applying Texture:", err);
            return reject(err);
          });
      });
    });

    return Promise.all(promises)
      .then((texturesArray) => {
        const textures = {};
        texturesArray.forEach(
          ({ name, texture }) => (textures[name] = texture)
        );
        return textures;
      })
      .catch((err) => console.log("loadMultipleTextures: ", err));
  }

  async applyGeoUpdates(geometry_updates, mainComponent) {
    const modelMesh = this.modelObj.children[0];
    for (const geometry_update of geometry_updates) {
      const component_geometry_updates = this.pdm.components?.[mainComponent]?.geometry_updates;
      if(this.pdm.geometry_updates.swatches[geometry_update] && 
        component_geometry_updates?.includes(geometry_update)) {
        const newGeos = this.pdm.geometry_updates.swatches[geometry_update].add_geo;
        const removeGeos = this.pdm.geometry_updates.swatches[geometry_update].remove_geo;
        for( const componentName of removeGeos) {
          const removeGeoObj = modelMesh.getObjectByName(componentName);
          if (removeGeoObj) {
            modelMesh.remove(removeGeoObj);
            this.sceneHelpers.disposeMeshObject({ meshObject: removeGeoObj });
          }
        }
        if(!this.pdm.merchandising_rules?.swatches?.[geometry_update]?.default_state) {
          for( const geo of newGeos) {
            const modelSKU = this.pdm.parent_model || this.pdm.sku;
            const size = this.$store.state.sceneSetup.modelBySize;
            const geoUrl = this.$store.getters["utils/addCacheBusting"](`${this.$store.state.endPoints.customizer.s3ModelsBucket}gltf_models/${modelSKU}/${size}/${geo.path}`);
            const newUGCGeo = await this.$model3D.loadObject(geoUrl);
            const geoObj = newUGCGeo.getObjectByName(geo.component_name);
            modelMesh.add(geoObj);
          }
        }
      }
    }
  } 

  disposeGLTF(gltfScene) {
    gltfScene.traverse((child) => {
      if (child.isMesh) {
        // Geometry
        if (child.geometry) {
          this.modelObj.remove(child);
          this.sceneHelpers.disposeMeshObject({ meshObject: child });
          child.geometry.dispose();
          child.geometry = null;
        }

        // Material(s)
        if (child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          this.sceneHelpers.disposeMaterial({
            materials
          });
          materials.forEach((mat) => {
            for (const key in mat) {
              const value = mat[key];
              if (value && value.isTexture) {
                value.dispose();
                mat[key] = null;
              }
            }
          });
          this.sceneHelpers.disposeTexture({ textureObj: child });
          child?.material?.dispose();
          child.material = null;
        }
      }
    });
  }

  delayTimer(ms) {
    this.animate3DModel();
    return new Promise((res) => setTimeout(res, ms));
  }

  created() {
    this.createScene(this.modelName);
  }

  destroyScene() {
    // Remove from scene
    if (this.modelObj) {
      this.scene.remove(this.modelObj);
      this.disposeGLTF(this.modelObj);
      this.modelObj = null;
    }

    // Dispose scene
    if (this.scene) {
      this.scene.traverse((child) => this.disposeGLTF(child));
      this.scene = null;
    }

    // Dispose renderer
    if (this.renderer) {
      this.renderer.forceContextLoss(); // GPU memory release
      this.renderer.dispose();
      this.renderer = null;
    }
    this.dracoLoader.dispose();
    this.objLoader.dracoLoader.dispose();
    this.camera = null;
  }

  beforeDestroy() {
    this.destroyScene();
  }
}
</script>
